/**
 * Public `/legal/partner-terms`: the Partner Terms, readable before signing
 * in (OrgSetup links here in a new tab so the half-filled form survives) and
 * linkable from anywhere.
 *
 * `/legal/dpa` no longer shows the DPA (owner decision 2026-09-26: the DPA is
 * available on request only). It shows a short note instead of a 404 because
 * earlier acceptances, emails and bookmarks point at it, and a partner that
 * accepted an earlier version incorporating a DPA should learn how to get a
 * copy. It is not gated behind sign-in either: any signed-in partner could
 * then read it, which would still be publishing it.
 */
import { useEffect } from 'react';
import { LEGAL_EMAIL, TERMS_PATH } from '../legal/current';
import { LEGAL_DOCUMENTS, LegalDocumentView } from '../legal/LegalDocumentView';
import { Link } from '../router';
import { Empty } from '../ui/kit';

function DpaOnRequest() {
  return (
    <article className="legal stack" aria-labelledby="legal-title">
      <h1 id="legal-title">Data processing addendum</h1>
      <p>
        FinderlyFix’s data processing addendum is available on request. Email{' '}
        <a href={`mailto:${LEGAL_EMAIL}?subject=Data%20processing%20addendum`}>{LEGAL_EMAIL}</a> and we will send it for
        signature. Once both parties sign it, it controls over the Partner Terms on data protection.
      </p>
      <p>
        You do not need it to use FinderlyFix: section 6 of the <a href={TERMS_PATH}>FinderlyFix Partner Terms</a> already
        sets out how we handle homeowner information as your service provider.
      </p>
      <p>If your organisation accepted an earlier version of the Partner Terms that included a DPA, we can send you a copy of that version.</p>
    </article>
  );
}

export function LegalPage({ doc }: { doc: string }) {
  const found = LEGAL_DOCUMENTS[doc];
  // A link to a section (`/legal/partner-terms#data`) lands on it: the page is
  // drawn after the browser tried to scroll there.
  useEffect(() => {
    const id = decodeURIComponent(window.location.hash.slice(1));
    if (id) document.getElementById(id)?.scrollIntoView?.();
  }, [doc]);
  return (
    <main className="shared-dashboard legal-page" id="main">
      <header className="row-between legal-head">
        <Link to="/" className="eyebrow legal-home">
          FinderlyFix Partners
        </Link>
        {/* The way back, said in words (the wordmark alone was not read as a link; second audit, 2026-09-27). */}
        <Link to="/" className="btn btn-sm btn-ghost legal-back">
          <span aria-hidden="true">←</span> Back to portal
        </Link>
      </header>
      {doc === 'dpa' ? (
        <DpaOnRequest />
      ) : found ? (
        <>
          <LegalDocumentView doc={found} toc />
          <p className="small muted">
            A data processing addendum is available on request. Questions: <a href={`mailto:${LEGAL_EMAIL}`}>{LEGAL_EMAIL}</a>
          </p>
        </>
      ) : (
        <Empty title="Document not found" />
      )}
    </main>
  );
}
